<script setup>
// Criar Template da Meta direto pela caixa (so id 1) — replica o formulario
// do gerenciador do WhatsApp: nome, categoria, idioma, cabecalho (texto/
// imagem/video/documento/localizacao), corpo com variaveis {{n}} + exemplos,
// rodape, botoes (resposta rapida, URL, telefone, codigo de copia) e o modo
// Autenticacao (OTP copiar codigo / one-tap). Envia o payload exato da Graph
// API pro backend, que repassa pra Meta.
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxTemplatesAPI from 'dashboard/api/inboxTemplates';

const props = defineProps({
  inbox: {
    type: Object,
    default: null,
  },
  // Menu Meta (Kairo 03/07/2026): criar o MESMO template em varias caixas de
  // uma vez. Quando presente, vence o prop inbox; a midia de exemplo e
  // reenviada por caixa (o handle da Meta e por app/caixa).
  inboxes: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['close', 'created']);

const targetInboxes = computed(() => {
  if (props.inboxes?.length) return props.inboxes;
  return props.inbox ? [props.inbox] : [];
});

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utilidade' },
  { value: 'AUTHENTICATION', label: 'Autenticação' },
];

// Idiomas aceitos pela Meta para templates (mesma lista do gerenciador).
const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (BR)' },
  { value: 'pt_PT', label: 'Português (POR)' },
  { value: 'en', label: 'Inglês' },
  { value: 'en_US', label: 'Inglês (EUA)' },
  { value: 'en_GB', label: 'Inglês (Reino Unido)' },
  { value: 'es', label: 'Espanhol' },
  { value: 'es_AR', label: 'Espanhol (ARG)' },
  { value: 'es_ES', label: 'Espanhol (ESP)' },
  { value: 'es_MX', label: 'Espanhol (MEX)' },
  { value: 'af', label: 'Africâner' },
  { value: 'sq', label: 'Albanês' },
  { value: 'de', label: 'Alemão' },
  { value: 'ar', label: 'Árabe' },
  { value: 'az', label: 'Azerbaijano' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bg', label: 'Búlgaro' },
  { value: 'ca', label: 'Catalão' },
  { value: 'zh_CN', label: 'Chinês (CHN)' },
  { value: 'zh_HK', label: 'Chinês (HKG)' },
  { value: 'zh_TW', label: 'Chinês (TAI)' },
  { value: 'ko', label: 'Coreano' },
  { value: 'hr', label: 'Croata' },
  { value: 'da', label: 'Dinamarquês' },
  { value: 'sk', label: 'Eslovaco' },
  { value: 'sl', label: 'Esloveno' },
  { value: 'et', label: 'Estoniano' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finlandês' },
  { value: 'fr', label: 'Francês' },
  { value: 'ka', label: 'Georgiano' },
  { value: 'el', label: 'Grego' },
  { value: 'gu', label: 'Guzerate' },
  { value: 'ha', label: 'Hauçá' },
  { value: 'he', label: 'Hebraico' },
  { value: 'hi', label: 'Hindi' },
  { value: 'nl', label: 'Holandês' },
  { value: 'hu', label: 'Húngaro' },
  { value: 'id', label: 'Indonésio' },
  { value: 'ga', label: 'Irlandês' },
  { value: 'it', label: 'Italiano' },
  { value: 'ja', label: 'Japonês' },
  { value: 'kn', label: 'Canarim' },
  { value: 'kk', label: 'Cazaque' },
  { value: 'ky_KG', label: 'Quirguiz' },
  { value: 'lo', label: 'Laosiano' },
  { value: 'lv', label: 'Letão' },
  { value: 'lt', label: 'Lituano' },
  { value: 'mk', label: 'Macedônio' },
  { value: 'ms', label: 'Malaio' },
  { value: 'ml', label: 'Malaiala' },
  { value: 'mr', label: 'Marati' },
  { value: 'nb', label: 'Norueguês' },
  { value: 'fa', label: 'Persa' },
  { value: 'pl', label: 'Polonês' },
  { value: 'pa', label: 'Panjabi' },
  { value: 'ro', label: 'Romeno' },
  { value: 'ru', label: 'Russo' },
  { value: 'rw_RW', label: 'Quiniaruanda' },
  { value: 'sr', label: 'Sérvio' },
  { value: 'sw', label: 'Suaíli' },
  { value: 'sv', label: 'Sueco' },
  { value: 'ta', label: 'Tâmil' },
  { value: 'cs', label: 'Tcheco' },
  { value: 'te', label: 'Télugo' },
  { value: 'th', label: 'Tailandês' },
  { value: 'tr', label: 'Turco' },
  { value: 'uk', label: 'Ucraniano' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbeque' },
  { value: 'vi', label: 'Vietnamita' },
  { value: 'zu', label: 'Zulu' },
];

const HEADER_FORMATS = [
  { value: 'NONE', label: 'Nenhum' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagem' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'DOCUMENT', label: 'Documento' },
  { value: 'LOCATION', label: 'Localização' },
];

const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

const name = ref('');
const category = ref('MARKETING');
const language = ref('pt_BR');
const allowCategoryChange = ref(true);

const headerFormat = ref('NONE');
const headerText = ref('');
const headerTextExample = ref('');
const headerHandle = ref('');
const headerFileName = ref('');
const headerFile = ref(null);
const isUploadingMedia = ref(false);
// Resultado por caixa no modo multi-caixa: { id, name, ok, error }
const results = ref([]);

const bodyText = ref('');
const bodyExamples = ref([]);
const footerText = ref('');

const buttons = ref([]);

// Autenticacao (OTP)
const authOtpType = ref('COPY_CODE');
const authButtonText = ref('Copiar código');
const authAutofillText = ref('Preencher automaticamente');
const authPackageName = ref('');
const authSignatureHash = ref('');
const authSecurityRecommendation = ref(true);
const authExpirationEnabled = ref(false);
const authExpirationMinutes = ref(10);

const isSaving = ref(false);
const serverError = ref('');

const isAuth = computed(() => category.value === 'AUTHENTICATION');
const isMediaHeader = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value)
);

const normalizedName = computed(() =>
  name.value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
);

const headerHasVariable = computed(() => /{{\s*1\s*}}/.test(headerText.value));

const bodyVariableCount = computed(() => {
  const matches = bodyText.value.match(/{{\s*(\d+)\s*}}/g) || [];
  const indexes = matches
    .map(m => parseInt(m.replace(/\D/g, ''), 10))
    .filter(n => n > 0);
  return indexes.length ? Math.max(...indexes) : 0;
});

const bodyVariablesSequential = computed(() => {
  const matches = bodyText.value.match(/{{\s*(\d+)\s*}}/g) || [];
  const indexes = [...new Set(matches.map(m => parseInt(m.replace(/\D/g, ''), 10)))];
  if (!indexes.length) return true;
  indexes.sort((a, b) => a - b);
  return indexes[0] === 1 && indexes[indexes.length - 1] === indexes.length;
});

const urlButtonCount = computed(
  () => buttons.value.filter(b => b.type === 'URL').length
);
const phoneButtonCount = computed(
  () => buttons.value.filter(b => b.type === 'PHONE_NUMBER').length
);
const copyCodeButtonCount = computed(
  () => buttons.value.filter(b => b.type === 'COPY_CODE').length
);

const canAddButton = type => {
  if (buttons.value.length >= 10) return false;
  if (type === 'URL') return urlButtonCount.value < 2;
  if (type === 'PHONE_NUMBER') return phoneButtonCount.value < 1;
  if (type === 'COPY_CODE') return copyCodeButtonCount.value < 1;
  return true;
};

const buttonsValid = computed(() =>
  buttons.value.every(b => {
    if (b.type === 'QUICK_REPLY') return !!b.text.trim();
    if (b.type === 'URL') {
      if (!b.text.trim() || !b.url.trim()) return false;
      if (/{{\s*1\s*}}/.test(b.url) && !b.example.trim()) return false;
      return true;
    }
    if (b.type === 'PHONE_NUMBER') return !!b.text.trim() && !!b.phoneNumber.trim();
    if (b.type === 'COPY_CODE') return !!b.example.trim();
    return false;
  })
);

const canSubmit = computed(() => {
  if (!normalizedName.value || isSaving.value) return false;
  if (isAuth.value) {
    if (!authButtonText.value.trim()) return false;
    if (
      authOtpType.value === 'ONE_TAP' &&
      (!authPackageName.value.trim() || !authSignatureHash.value.trim())
    )
      return false;
    return true;
  }
  if (!bodyText.value.trim() || !bodyVariablesSequential.value) return false;
  for (let i = 0; i < bodyVariableCount.value; i += 1) {
    if (!(bodyExamples.value[i] || '').trim()) return false;
  }
  if (headerFormat.value === 'TEXT') {
    if (!headerText.value.trim()) return false;
    if (headerHasVariable.value && !headerTextExample.value.trim()) return false;
  }
  if (isMediaHeader.value && !headerHandle.value) return false;
  if (!buttonsValid.value) return false;
  return true;
});

const onHeaderFormatChange = () => {
  headerHandle.value = '';
  headerFileName.value = '';
};

const onMediaSelected = async event => {
  const [file] = event.target.files || [];
  if (!file || !targetInboxes.value.length) return;
  isUploadingMedia.value = true;
  serverError.value = '';
  try {
    // Sobe pra 1a caixa (valida cedo); no multi-caixa o submit reenvia o
    // arquivo pras demais (handle da Meta e por app/caixa).
    const { data } = await InboxTemplatesAPI.uploadTemplateMedia(
      targetInboxes.value[0].id,
      file
    );
    headerHandle.value = data.handle;
    headerFileName.value = file.name;
    headerFile.value = file;
    useAlert('Arquivo de exemplo enviado para a Meta.');
  } catch (e) {
    headerHandle.value = '';
    headerFileName.value = '';
    headerFile.value = null;
    serverError.value = e?.response?.data?.error || e.message;
  } finally {
    isUploadingMedia.value = false;
    // permite reenviar o mesmo arquivo
    event.target.value = '';
  }
};

const addButton = type => {
  if (!canAddButton(type)) return;
  buttons.value.push({
    type,
    text:
      type === 'PHONE_NUMBER' ? 'Ligar' : type === 'URL' ? 'Acessar site' : '',
    url: '',
    example: '',
    phoneNumber: '',
  });
};

const removeButton = index => {
  buttons.value.splice(index, 1);
};

// A Meta exige os botoes de resposta rapida agrupados (contiguos).
const orderedButtons = () => {
  const quickReplies = buttons.value.filter(b => b.type === 'QUICK_REPLY');
  const others = buttons.value.filter(b => b.type !== 'QUICK_REPLY');
  if (buttons.value[0]?.type === 'QUICK_REPLY') {
    return [...quickReplies, ...others];
  }
  return [...others, ...quickReplies];
};

const buildComponents = (handleOverride = null) => {
  if (isAuth.value) {
    const components = [
      { type: 'BODY', add_security_recommendation: authSecurityRecommendation.value },
    ];
    if (authExpirationEnabled.value) {
      components.push({
        type: 'FOOTER',
        code_expiration_minutes: Number(authExpirationMinutes.value),
      });
    }
    const otpButton =
      authOtpType.value === 'ONE_TAP'
        ? {
            type: 'OTP',
            otp_type: 'ONE_TAP',
            text: authButtonText.value.trim(),
            autofill_text: authAutofillText.value.trim(),
            package_name: authPackageName.value.trim(),
            signature_hash: authSignatureHash.value.trim(),
          }
        : {
            type: 'OTP',
            otp_type: 'COPY_CODE',
            text: authButtonText.value.trim(),
          };
    components.push({ type: 'BUTTONS', buttons: [otpButton] });
    return components;
  }

  const components = [];
  if (headerFormat.value === 'TEXT') {
    const header = { type: 'HEADER', format: 'TEXT', text: headerText.value.trim() };
    if (headerHasVariable.value) {
      header.example = { header_text: [headerTextExample.value.trim()] };
    }
    components.push(header);
  } else if (isMediaHeader.value) {
    components.push({
      type: 'HEADER',
      format: headerFormat.value,
      example: { header_handle: [handleOverride || headerHandle.value] },
    });
  } else if (headerFormat.value === 'LOCATION') {
    components.push({ type: 'HEADER', format: 'LOCATION' });
  }

  const body = { type: 'BODY', text: bodyText.value.trim() };
  if (bodyVariableCount.value > 0) {
    body.example = {
      body_text: [
        bodyExamples.value
          .slice(0, bodyVariableCount.value)
          .map(e => (e || '').trim()),
      ],
    };
  }
  components.push(body);

  if (footerText.value.trim()) {
    components.push({ type: 'FOOTER', text: footerText.value.trim() });
  }

  if (buttons.value.length) {
    components.push({
      type: 'BUTTONS',
      buttons: orderedButtons().map(b => {
        if (b.type === 'QUICK_REPLY') {
          return { type: 'QUICK_REPLY', text: b.text.trim() };
        }
        if (b.type === 'URL') {
          const button = { type: 'URL', text: b.text.trim(), url: b.url.trim() };
          if (/{{\s*1\s*}}/.test(b.url)) button.example = [b.example.trim()];
          return button;
        }
        if (b.type === 'PHONE_NUMBER') {
          return {
            type: 'PHONE_NUMBER',
            text: b.text.trim(),
            phone_number: b.phoneNumber.trim(),
          };
        }
        return { type: 'COPY_CODE', example: b.example.trim() };
      }),
    });
  }

  return components;
};

// Cria o template em TODAS as caixas-alvo, uma a uma. No multi-caixa a midia
// e reenviada por caixa (handle e por app) e o resultado aparece por caixa;
// o modal so fecha sozinho se TODAS derem certo.
const submit = async () => {
  if (!canSubmit.value) return;
  isSaving.value = true;
  serverError.value = '';
  results.value = [];
  const base = {
    name: normalizedName.value,
    language: language.value,
    category: category.value,
    allow_category_change: allowCategoryChange.value,
  };
  const firstId = targetInboxes.value[0]?.id;
  for (const box of targetInboxes.value) {
    try {
      let handle = null;
      if (isMediaHeader.value && box.id !== firstId) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await InboxTemplatesAPI.uploadTemplateMedia(
          box.id,
          headerFile.value
        );
        handle = data.handle;
      }
      // eslint-disable-next-line no-await-in-loop
      await InboxTemplatesAPI.createTemplate(box.id, {
        ...base,
        components: buildComponents(handle),
      });
      results.value.push({ id: box.id, name: box.name, ok: true });
    } catch (e) {
      results.value.push({
        id: box.id,
        name: box.name,
        ok: false,
        error: e?.response?.data?.error || e.message,
      });
    }
  }
  isSaving.value = false;
  const allOk = results.value.every(r => r.ok);
  if (targetInboxes.value.length === 1) {
    if (allOk) {
      useAlert('Template enviado para análise da Meta.');
      emit('created');
    } else {
      serverError.value = results.value[0]?.error || 'Falha ao criar template';
    }
  } else if (allOk) {
    useAlert(`Template criado nas ${results.value.length} caixas.`);
    emit('created');
  } else {
    useAlert('Criação concluída com erros — veja o resultado por caixa.');
  }
};

// Monta o token {{n}} em JS — interpolar chaves duplas direto no template
// quebraria o parser do Vue.
const varToken = i => `{{${i}}}`;

const inputClass =
  'w-full h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand';
const selectClass =
  'w-full h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none !mb-0';
const textareaClass =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="p-6 max-h-[85vh] overflow-y-auto">
      <h3 class="text-lg font-medium text-n-slate-12 mb-1">Criar Template</h3>
      <p class="text-sm text-n-slate-11 mb-5">
        O template é criado direto na Meta com as credenciais desta caixa e
        entra em análise. O status aparece na lista assim que criado.
      </p>

      <div class="flex flex-col gap-4">
        <!-- Nome / categoria / idioma -->
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-1 block">
            Nome do template
          </label>
          <input
            v-model="name"
            :class="inputClass"
            maxlength="512"
            placeholder="ex: promocao_julho"
          />
          <p v-if="name && normalizedName !== name" class="text-xs text-n-slate-10 mt-1">
            Será criado como: <span class="font-mono">{{ normalizedName }}</span>
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            Apenas letras minúsculas, números e underline (regra da Meta).
          </p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Categoria
            </label>
            <select v-model="category" :class="selectClass">
              <option v-for="c in CATEGORIES" :key="c.value" :value="c.value">
                {{ c.label }}
              </option>
            </select>
          </div>
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Idioma
            </label>
            <select v-model="language" :class="selectClass">
              <option v-for="l in LANGUAGES" :key="l.value" :value="l.value">
                {{ l.label }} ({{ l.value }})
              </option>
            </select>
          </div>
        </div>

        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="allowCategoryChange" type="checkbox" />
          Permitir que a Meta ajuste a categoria automaticamente (evita
          reprovação por categoria errada)
        </label>

        <!-- AUTENTICACAO (OTP) -->
        <template v-if="isAuth">
          <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-3">
            <p class="text-sm text-n-slate-11">
              Em templates de autenticação a Meta define o texto da mensagem
              automaticamente (código de acesso + validade). Você configura o
              tipo de botão do código:
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                  Entrega do código
                </label>
                <select v-model="authOtpType" :class="selectClass">
                  <option value="COPY_CODE">Copiar código</option>
                  <option value="ONE_TAP">One-tap (preenche no app Android)</option>
                </select>
              </div>
              <div>
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                  Texto do botão
                </label>
                <input v-model="authButtonText" :class="inputClass" maxlength="25" />
              </div>
            </div>
            <template v-if="authOtpType === 'ONE_TAP'">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                    Package name do app Android
                  </label>
                  <input
                    v-model="authPackageName"
                    :class="inputClass"
                    placeholder="com.exemplo.app"
                  />
                </div>
                <div>
                  <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                    Signature hash (11 caracteres)
                  </label>
                  <input v-model="authSignatureHash" :class="inputClass" maxlength="11" />
                </div>
              </div>
              <div>
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                  Texto do preenchimento automático
                </label>
                <input v-model="authAutofillText" :class="inputClass" maxlength="25" />
              </div>
            </template>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="authSecurityRecommendation" type="checkbox" />
              Adicionar recomendação de segurança ("não compartilhe este código")
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="authExpirationEnabled" type="checkbox" />
              Mostrar validade do código no rodapé
            </label>
            <div v-if="authExpirationEnabled" class="w-48">
              <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                Validade (minutos, 1-90)
              </label>
              <input
                v-model="authExpirationMinutes"
                type="number"
                min="1"
                max="90"
                :class="inputClass"
              />
            </div>
          </div>
        </template>

        <!-- MARKETING / UTILIDADE -->
        <template v-else>
          <!-- Cabecalho -->
          <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-3">
            <label class="text-sm font-medium text-n-slate-12 block">
              Cabeçalho (opcional)
            </label>
            <select
              v-model="headerFormat"
              :class="selectClass"
              class="max-w-xs"
              @change="onHeaderFormatChange"
            >
              <option v-for="h in HEADER_FORMATS" :key="h.value" :value="h.value">
                {{ h.label }}
              </option>
            </select>

            <template v-if="headerFormat === 'TEXT'">
              <input
                v-model="headerText"
                :class="inputClass"
                maxlength="60"
                placeholder="Texto do cabeçalho — pode usar {{1}}"
              />
              <div v-if="headerHasVariable">
                <label class="text-sm font-medium text-n-slate-12 mb-1 block">
                  Exemplo para a variável do cabeçalho
                </label>
                <input v-model="headerTextExample" :class="inputClass" />
              </div>
            </template>

            <template v-if="isMediaHeader">
              <input
                type="file"
                :accept="MEDIA_ACCEPT[headerFormat]"
                class="text-sm text-n-slate-11"
                :disabled="isUploadingMedia"
                @change="onMediaSelected"
              />
              <p v-if="isUploadingMedia" class="text-xs text-n-amber-11">
                Enviando arquivo de exemplo para a Meta…
              </p>
              <p v-else-if="headerHandle" class="text-xs text-n-teal-11">
                ✓ Exemplo enviado: {{ headerFileName }}
              </p>
              <p v-else class="text-xs text-n-slate-10">
                A Meta exige um arquivo de exemplo para aprovar o template
                ({{ headerFormat === 'IMAGE' ? 'JPG/PNG' : headerFormat === 'VIDEO' ? 'MP4' : 'PDF' }}).
              </p>
            </template>
          </div>

          <!-- Corpo -->
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Corpo da mensagem
            </label>
            <textarea
              v-model="bodyText"
              :class="textareaClass"
              rows="5"
              maxlength="1024"
              placeholder="Olá {{1}}, sua proposta {{2}} foi aprovada!"
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ bodyText.length }}/1024 — use variáveis como
              <span class="font-mono">{{ varToken(1) }}</span>,
              <span class="font-mono">{{ varToken(2) }}</span>…
            </p>
            <p v-if="!bodyVariablesSequential" class="text-xs text-n-ruby-11 mt-1">
              As variáveis precisam ser sequenciais começando em 1 (regra da Meta).
            </p>
          </div>

          <div v-if="bodyVariableCount > 0" class="rounded-lg border border-n-weak p-4">
            <label class="text-sm font-medium text-n-slate-12 mb-2 block">
              Exemplos das variáveis (obrigatório pela Meta)
            </label>
            <div class="flex flex-col gap-2">
              <div
                v-for="i in bodyVariableCount"
                :key="i"
                class="flex items-center gap-2"
              >
                <span class="text-sm font-mono text-n-slate-11 w-14">
                  {{ varToken(i) }}
                </span>
                <input
                  v-model="bodyExamples[i - 1]"
                  :class="inputClass"
                  :placeholder="`Exemplo para a variável ${i}`"
                />
              </div>
            </div>
          </div>

          <!-- Rodape -->
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Rodapé (opcional)
            </label>
            <input
              v-model="footerText"
              :class="inputClass"
              maxlength="60"
              placeholder="Texto pequeno no fim da mensagem"
            />
          </div>

          <!-- Botoes -->
          <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                Botões (opcional) — {{ buttons.length }}/10
              </label>
              <div class="flex gap-2 flex-wrap">
                <NextButton
                  size="xs"
                  variant="outline"
                  label="+ Resposta rápida"
                  :disabled="!canAddButton('QUICK_REPLY')"
                  @click="addButton('QUICK_REPLY')"
                />
                <NextButton
                  size="xs"
                  variant="outline"
                  label="+ URL"
                  :disabled="!canAddButton('URL')"
                  @click="addButton('URL')"
                />
                <NextButton
                  size="xs"
                  variant="outline"
                  label="+ Telefone"
                  :disabled="!canAddButton('PHONE_NUMBER')"
                  @click="addButton('PHONE_NUMBER')"
                />
                <NextButton
                  size="xs"
                  variant="outline"
                  label="+ Código de cópia"
                  :disabled="!canAddButton('COPY_CODE')"
                  @click="addButton('COPY_CODE')"
                />
              </div>
            </div>

            <div
              v-for="(button, index) in buttons"
              :key="index"
              class="rounded-lg bg-n-alpha-black1 border border-n-weak p-3 flex flex-col gap-2"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs font-medium text-n-slate-11 uppercase">
                  {{
                    button.type === 'QUICK_REPLY'
                      ? 'Resposta rápida'
                      : button.type === 'URL'
                        ? 'URL'
                        : button.type === 'PHONE_NUMBER'
                          ? 'Telefone'
                          : 'Código de cópia'
                  }}
                </span>
                <NextButton
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  label="Remover"
                  @click="removeButton(index)"
                />
              </div>

              <template v-if="button.type === 'QUICK_REPLY'">
                <input
                  v-model="button.text"
                  :class="inputClass"
                  maxlength="25"
                  placeholder="Texto do botão"
                />
              </template>

              <template v-else-if="button.type === 'URL'">
                <div class="grid grid-cols-2 gap-2">
                  <input
                    v-model="button.text"
                    :class="inputClass"
                    maxlength="25"
                    placeholder="Texto do botão"
                  />
                  <input
                    v-model="button.url"
                    :class="inputClass"
                    maxlength="2000"
                    placeholder="https://exemplo.com/{{1}}"
                  />
                </div>
                <input
                  v-if="/{{\s*1\s*}}/.test(button.url)"
                  v-model="button.example"
                  :class="inputClass"
                  placeholder="URL completa de exemplo (obrigatório pela Meta)"
                />
              </template>

              <template v-else-if="button.type === 'PHONE_NUMBER'">
                <div class="grid grid-cols-2 gap-2">
                  <input
                    v-model="button.text"
                    :class="inputClass"
                    maxlength="25"
                    placeholder="Texto do botão"
                  />
                  <input
                    v-model="button.phoneNumber"
                    :class="inputClass"
                    maxlength="20"
                    placeholder="+5521999999999"
                  />
                </div>
              </template>

              <template v-else>
                <input
                  v-model="button.example"
                  :class="inputClass"
                  maxlength="15"
                  placeholder="Código de exemplo (ex: CUPOM10)"
                />
              </template>
            </div>
          </div>
        </template>

        <p v-if="serverError" class="text-sm text-n-ruby-11 whitespace-pre-wrap">
          {{ serverError }}
        </p>

        <div
          v-if="results.length && targetInboxes.length > 1"
          class="rounded-lg border border-n-weak p-3 flex flex-col gap-1"
        >
          <p class="text-xs font-medium text-n-slate-11">
            Resultado por caixa:
          </p>
          <p
            v-for="r in results"
            :key="r.id"
            class="text-xs"
            :class="r.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ r.ok ? '✅' : '❌' }} {{ r.name }}{{ r.ok ? ' — enviado para análise' : ` — ${r.error}` }}
          </p>
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <NextButton
            color="slate"
            variant="outline"
            label="Cancelar"
            @click="emit('close')"
          />
          <NextButton
            label="Criar template"
            :is-loading="isSaving"
            :disabled="!canSubmit"
            @click="submit"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
